import React, { useState } from "react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, ArrowRight, Workflow, BellRing, Users,
  Handshake, PlayCircle, CheckCircle2, Layers, Zap, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { STEPS as WORKFLOW_STEPS } from "@/lib/steps";

const FEATURES = [
  { icon: Workflow, title: "Multi-stage pipeline", text: "One owner per stage, initiation to closure.",
    badge: "from-blue-500/25 to-cyan-400/10", iconColor: "text-cyan-300" },
  { icon: Users, title: "Role-based dashboards", text: "Every stakeholder sees only what's theirs.",
    badge: "from-indigo-500/25 to-blue-400/10", iconColor: "text-indigo-300" },
  { icon: BellRing, title: "Automated reminders", text: "Hand-offs and SLA nudges, sent automatically.",
    badge: "from-amber-500/25 to-orange-400/10", iconColor: "text-amber-300" },
];

const STATS = [
  { icon: Layers, label: `${WORKFLOW_STEPS.length}-Stage Workflow`, color: "border-cyan-400/20 bg-cyan-400/5 text-cyan-300" },
  { icon: Zap, label: "Real-time Hand-offs", color: "border-blue-400/20 bg-blue-400/5 text-blue-300" },
  { icon: ShieldCheck, label: "Full Audit Trail", color: "border-emerald-400/20 bg-emerald-400/5 text-emerald-300" },
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to Trackly");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark relative min-h-screen overflow-x-hidden bg-[#050914] text-foreground">
      {/* Ambient background: glow orbs + grid */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[34rem] w-[34rem] rounded-full opacity-25 blur-3xl nf-float"
          style={{ background: "radial-gradient(circle, hsl(221 83% 45%) 0%, transparent 65%)" }} />
        <div className="absolute -bottom-52 right-[-8rem] h-[38rem] w-[38rem] rounded-full opacity-20 blur-3xl nf-float-delay"
          style={{ background: "radial-gradient(circle, hsl(188 86% 45%) 0%, transparent 65%)" }} />
        <div className="absolute top-1/3 right-[8%] h-[24rem] w-[24rem] rounded-full opacity-[0.14] blur-3xl nf-float"
          style={{ background: "radial-gradient(circle, hsl(210 90% 55%) 0%, transparent 65%)", animationDelay: "4s" }} />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(210 40% 80% / 0.5) 1px, transparent 1px), linear-gradient(90deg, hsl(210 40% 80% / 0.5) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }} />
        <div className="absolute inset-x-0 top-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, hsl(188 86% 53% / 0.6), transparent)" }} />
      </div>

      <div className="relative z-10 mx-auto grid min-h-screen max-w-7xl lg:grid-cols-[1.15fr_1fr]">
        {/* Left: hero */}
        <div className="relative hidden flex-col justify-center gap-8 p-10 xl:p-12 lg:flex">
          <div className="flex items-center gap-3">
            <div className="grid h-14 place-items-center rounded-xl bg-white/95 px-3 py-2 shadow-lg shadow-cyan-500/10">
              <img src="/assets/nexusiq-logo.png" alt="NexusIQ Solutions" className="h-8 w-auto object-contain" />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold tracking-tight text-white">Trackly</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400">NexusIQ Solutions · Internal</div>
            </div>
          </div>

          <div className="max-w-xl space-y-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.2em] text-cyan-300">
                Corporate training lifecycle
              </div>
              <p className="max-w-lg text-[14px] leading-relaxed text-slate-400">
                Real-time visibility, role-based ownership, and zero dropped hand-offs.
              </p>
              <div className="flex flex-wrap gap-2">
                {STATS.map(({ icon: Icon, label, color }) => (
                  <span key={label}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium ${color}`}>
                    <Icon className="h-3 w-3" /> {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Pipeline motif */}
            <div className="max-w-md">
              <div className="flex items-center">
                {WORKFLOW_STEPS.map((_, i) => (
                  <React.Fragment key={i}>
                    <span
                      className={
                        i < 4
                          ? "h-2.5 w-2.5 shrink-0 rounded-full bg-cyan-400 shadow-[0_0_10px_hsl(188_86%_53%/0.8)]"
                          : i === 4
                            ? "nf-pulse h-3 w-3 shrink-0 rounded-full bg-blue-400"
                            : "h-2 w-2 shrink-0 rounded-full bg-slate-700"
                      }
                    />
                    {i < WORKFLOW_STEPS.length - 1 && (
                      <span className={`h-px flex-1 ${i < 4 ? "bg-cyan-400/50" : "bg-slate-800"}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-medium uppercase tracking-widest text-slate-500">
                <span className="flex items-center gap-1"><Handshake className="h-3 w-3" /> Initiation</span>
                <span className="flex items-center gap-1 text-blue-300"><PlayCircle className="h-3 w-3" /> Execution</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Closure</span>
              </div>
            </div>

            {/* Feature cards */}
            <div className="grid gap-2.5">
              {FEATURES.map(({ icon: Icon, title, text, badge, iconColor }) => (
                <div key={title}
                  className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3 backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/[0.05]">
                  <div className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/10 bg-gradient-to-br ${badge}`}>
                    <Icon className={`h-4 w-4 ${iconColor}`} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{title}</div>
                    <div className="text-[13px] leading-relaxed text-slate-400">{text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: sign-in */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            {/* Compact brand for mobile */}
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <div className="grid h-12 place-items-center rounded-xl bg-white/95 px-2.5 py-1.5">
                <img src="/assets/nexusiq-logo.png" alt="NexusIQ Solutions" className="h-6 w-auto object-contain" />
              </div>
              <div>
                <div className="font-heading text-xl font-bold tracking-tight text-white">Trackly</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">NexusIQ Solutions · Internal</div>
              </div>
            </div>

            <div className="relative">
              {/* Cute robot mascot — sits perched on the credentials card */}
              <img
                src="/assets/robot-mascot.webp"
                alt=""
                aria-hidden="true"
                className="nf-float pointer-events-none absolute -top-14 right-6 z-10 h-24 w-24 object-contain drop-shadow-[0_0_30px_rgba(56,189,248,0.4)]"
              />
              <div aria-hidden className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-blue-500/40 via-cyan-400/30 to-violet-500/40 opacity-60 blur-lg" />
              <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-2xl shadow-black/40 backdrop-blur-xl">
              <h2 className="font-heading text-[26px] font-semibold tracking-tight text-white">Welcome back</h2>
              <p className="mt-1.5 text-sm text-slate-400">Sign in with your NexusIQ team credentials.</p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input id="email" data-testid="login-email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                    className="h-11 border-white/10 bg-white/[0.04] text-white placeholder:text-slate-500 focus-visible:ring-cyan-400/60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input id="password" data-testid="login-password" type={showPassword ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                      className="h-11 border-white/10 bg-white/[0.04] pr-10 text-white focus-visible:ring-cyan-400/60" />
                    <button
                      type="button"
                      data-testid="login-password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-200"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button data-testid="login-submit" type="submit" disabled={loading}
                  className="group h-11 w-full border-0 text-[15px] font-semibold text-white shadow-lg shadow-blue-900/40 transition-all hover:shadow-cyan-500/25"
                  style={{ background: "linear-gradient(90deg, hsl(221 83% 48%), hsl(199 89% 44%))" }}>
                  {loading ? "Signing in..." : (
                    <span className="flex items-center justify-center gap-2">
                      Sign in
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  )}
                </Button>
              </form>
              </div>
            </div>

            <div className="mt-6 flex flex-col items-center gap-2 text-xs text-slate-500 sm:flex-row sm:justify-between">
              <span>© {new Date().getFullYear()} NexusIQ Solutions Pvt. Ltd.</span>
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-cyan-400/70" /> Secure JWT · role-based access
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
